/*
 * Updated Dashboard component to implement role-based visibility
 * - Added userRole state to track the current admin user's role
 * - Fetches user role from admin_users table on component mount
 * - Monthly Revenue and Yearly Revenue cards are now only visible to superadmin users
 * - Grid layout adjusts dynamically based on visible cards (4 columns for superadmin, 2 columns for regular admin)
 * - Updated: Commission now calculated per-tour based on commission_type and commission_value
 *   (percentage of total price, or 8.5% of per-person amount). Cancelled bookings excluded.
 * - Added per-tour commission breakdown card for superadmins.
 * - Updated: Admins now see total booking revenue (not commission). Superadmins see commission
 *   cards plus an export button that opens a month-selection modal to generate a PDF report.
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Image, Images, Calendar, FileText, Newspaper, ChevronLeft, ChevronRight, TrendingUp, TrendingDown, DollarSign, Users, FileDown } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import BookingCalendar from '../../components/admin/BookingCalendar';
import CommissionReportModal from '../../components/admin/CommissionReportModal';
import { supabase } from '../../lib/supabase';

interface TourCommissionInfo {
  commission_type: 'percentage' | 'per_person' | null;
  commission_value: number | null;
}

interface BookingWithTour {
  total_price: number;
  persons: number;
  status: string;
  tours: { id: string; name: string; commission_type: 'percentage' | 'per_person' | null; commission_value: number | null } | null;
}

interface TourBreakdownItem {
  tourId: string;
  tourName: string;
  commission: number;
  bookingCount: number;
}

interface FinancialStats {
  monthlyRevenue: number;
  monthlyBookings: number;
  monthlyTotalBookingRevenue: number;
  yearlyRevenue: number;
  previousMonthRevenue: number;
  previousMonthBookings: number;
  sameMonthLastYearRevenue: number;
  sameMonthLastYearBookings: number;
  previousMonthBookingRevenue: number;
  sameMonthLastYearBookingRevenue: number;
  tourBreakdown: TourBreakdownItem[];
}

const COMMISSION_RATE = 0.085;

const calculateBookingCommission = (booking: BookingWithTour): number => {
  const tour = booking.tours;
  if (!tour || !tour.commission_type || tour.commission_value == null) return 0;
  if (tour.commission_type === 'percentage') {
    return (tour.commission_value || 0) * (booking.total_price || 0);
  }
  // per_person: operator earns commission_value per person; platform takes 8.5% of that
  return COMMISSION_RATE * (tour.commission_value * (booking.persons || 0));
};

const Dashboard = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [stats, setStats] = useState<FinancialStats>({
    monthlyRevenue: 0,
    monthlyBookings: 0,
    monthlyTotalBookingRevenue: 0,
    yearlyRevenue: 0,
    previousMonthRevenue: 0,
    previousMonthBookings: 0,
    sameMonthLastYearRevenue: 0,
    sameMonthLastYearBookings: 0,
    previousMonthBookingRevenue: 0,
    sameMonthLastYearBookingRevenue: 0,
    tourBreakdown: [],
  });
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState<'admin' | 'superadmin' | null>(null);
  const [showReportModal, setShowReportModal] = useState(false);

  useEffect(() => {
    fetchUserRole();
    fetchFinancialStats();
  }, [currentDate]);

  const fetchUserRole = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (user) {
        const { data: adminUser } = await supabase
          .from('admin_users')
          .select('role')
          .eq('id', user.id)
          .maybeSingle();

        setUserRole(adminUser?.role || 'admin');
      }
    } catch (error) {
      console.error('Error fetching user role:', error);
      setUserRole('admin');
    }
  };

  const fetchFinancialStats = async () => {
    setLoading(true);
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();

      const startOfMonth = new Date(year, month, 1).toISOString();
      const endOfMonth = new Date(year, month + 1, 0, 23, 59, 59).toISOString();

      const startOfPrevMonth = new Date(year, month - 1, 1).toISOString();
      const endOfPrevMonth = new Date(year, month, 0, 23, 59, 59).toISOString();

      const startOfSameMonthLastYear = new Date(year - 1, month, 1).toISOString();
      const endOfSameMonthLastYear = new Date(year - 1, month + 1, 0, 23, 59, 59).toISOString();

      const startOfYear = new Date(year, 0, 1).toISOString();
      const endOfYear = new Date(year, 11, 31, 23, 59, 59).toISOString();

      const selectFields = 'total_price, persons, status, tours (id, name, commission_type, commission_value)';

      const [currentMonth, prevMonth, sameMonthLastYear, yearlyData] = await Promise.all([
        supabase
          .from('bookings')
          .select(selectFields)
          .gte('booking_date', startOfMonth)
          .lte('booking_date', endOfMonth)
          .neq('status', 'cancelled'),
        supabase
          .from('bookings')
          .select(selectFields)
          .gte('booking_date', startOfPrevMonth)
          .lte('booking_date', endOfPrevMonth)
          .neq('status', 'cancelled'),
        supabase
          .from('bookings')
          .select(selectFields)
          .gte('booking_date', startOfSameMonthLastYear)
          .lte('booking_date', endOfSameMonthLastYear)
          .neq('status', 'cancelled'),
        supabase
          .from('bookings')
          .select(selectFields)
          .gte('booking_date', startOfYear)
          .lte('booking_date', endOfYear)
          .neq('status', 'cancelled'),
      ]);

      const calculateRevenue = (bookings: BookingWithTour[]) =>
        bookings.reduce((sum, b) => sum + calculateBookingCommission(b), 0);

      const calculateBookingRevenue = (bookings: BookingWithTour[]) =>
        bookings.reduce((sum, b) => sum + (b.total_price || 0), 0);

      // Build per-tour breakdown for current month
      const breakdownMap = new Map<string, TourBreakdownItem>();
      (currentMonth.data || []).forEach((b) => {
        if (!b.tours) return;
        const key = b.tours.id;
        const existing = breakdownMap.get(key);
        const commission = calculateBookingCommission(b);
        if (existing) {
          existing.commission += commission;
          existing.bookingCount += 1;
        } else {
          breakdownMap.set(key, {
            tourId: key,
            tourName: b.tours.name,
            commission,
            bookingCount: 1,
          });
        }
      });

      setStats({
        monthlyRevenue: calculateRevenue(currentMonth.data || []),
        monthlyBookings: currentMonth.data?.length || 0,
        monthlyTotalBookingRevenue: calculateBookingRevenue(currentMonth.data || []),
        yearlyRevenue: calculateRevenue(yearlyData.data || []),
        previousMonthRevenue: calculateRevenue(prevMonth.data || []),
        previousMonthBookings: prevMonth.data?.length || 0,
        sameMonthLastYearRevenue: calculateRevenue(sameMonthLastYear.data || []),
        sameMonthLastYearBookings: sameMonthLastYear.data?.length || 0,
        previousMonthBookingRevenue: calculateBookingRevenue(prevMonth.data || []),
        sameMonthLastYearBookingRevenue: calculateBookingRevenue(sameMonthLastYear.data || []),
        tourBreakdown: Array.from(breakdownMap.values()).sort((a, b) => b.commission - a.commission),
      });
    } catch (error) {
      console.error('Error fetching financial stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      if (direction === 'prev') {
        newDate.setMonth(newDate.getMonth() - 1);
      } else {
        newDate.setMonth(newDate.getMonth() + 1);
      }
      return newDate;
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const calculateChange = (current: number, previous: number) => {
    if (previous === 0) return current > 0 ? 100 : 0;
    return ((current - previous) / previous) * 100;
  };

  const monthName = currentDate.toLocaleString('en-US', { month: 'long', year: 'numeric' });

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="space-y-8">
          <div>
            <h1 className="text-3xl font-bold mb-2">Dashboard</h1>
          </div>

          {/* Quick Links - Smaller */}
          <div className="flex flex-wrap gap-2">
            <Link
              to="/kh-admin/tours"
              className="bg-white/75 backdrop-blur-lg rounded-lg px-3 py-1.5 hover:bg-white transition-colors"
            >
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 bg-[#2A382A] rounded-lg flex items-center justify-center">
                  <MapPin className="w-4 h-4 text-white" />
                </div>
                <span className="text-sm font-medium text-[#2A382A]">Tours</span>
              </div>
            </Link>

            <Link
              to="/kh-admin/gallery"
              className="bg-white/75 backdrop-blur-lg rounded-lg px-3 py-1.5 hover:bg-white transition-colors"
            >
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 bg-[#2A382A] rounded-lg flex items-center justify-center">
                  <Image className="w-4 h-4 text-white" />
                </div>
                <span className="text-sm font-medium text-[#2A382A]">Gallery</span>
              </div>
            </Link>

            <Link
              to="/kh-admin/news"
              className="bg-white/75 backdrop-blur-lg rounded-lg px-3 py-1.5 hover:bg-white transition-colors"
            >
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 bg-[#2A382A] rounded-lg flex items-center justify-center">
                  <Newspaper className="w-4 h-4 text-white" />
                </div>
                <span className="text-sm font-medium text-[#2A382A]">News</span>
              </div>
            </Link>

            <Link
              to="/kh-admin/scientific-work"
              className="bg-white/75 backdrop-blur-lg rounded-lg px-3 py-1.5 hover:bg-white transition-colors"
            >
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 bg-[#2A382A] rounded-lg flex items-center justify-center">
                  <FileText className="w-4 h-4 text-white" />
                </div>
                <span className="text-sm font-medium text-[#2A382A]">Scientific Work</span>
              </div>
            </Link>

            <Link
              to="/kh-admin/hero"
              className="bg-white/75 backdrop-blur-lg rounded-lg px-3 py-1.5 hover:bg-white transition-colors"
            >
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 bg-[#2A382A] rounded-lg flex items-center justify-center">
                  <Images className="w-4 h-4 text-white" />
                </div>
                <span className="text-sm font-medium text-[#2A382A]">Hero</span>
              </div>
            </Link>

            <Link
              to="/kh-admin/bookings"
              className="bg-white/75 backdrop-blur-lg rounded-lg px-3 py-1.5 hover:bg-white transition-colors"
            >
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 bg-[#2A382A] rounded-lg flex items-center justify-center">
                  <Calendar className="w-4 h-4 text-white" />
                </div>
                <span className="text-sm font-medium text-[#2A382A]">Bookings</span>
              </div>
            </Link>
          </div>

          {/* Financial Statistics */}
          <div className="space-y-4">
            {/* Month Navigation */}
            <div className="flex items-center justify-between bg-[#1F2B1F] rounded-xl p-4">
              <button
                onClick={() => navigateMonth('prev')}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5 text-white" />
              </button>
              <h2 className="text-2xl font-bold text-white">{monthName}</h2>
              <button
                onClick={() => navigateMonth('next')}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <ChevronRight className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Stats Grid */}
            {loading ? (
              <div className="text-center text-white py-8">Loading statistics...</div>
            ) : (
              <div className={`grid grid-cols-1 md:grid-cols-2 ${userRole === 'superadmin' ? 'lg:grid-cols-4' : 'lg:grid-cols-3'} gap-4`}>
                {/* Monthly Commission - superadmin only */}
                {userRole === 'superadmin' && (
                <div className="bg-[#1F2B1F] rounded-xl p-6 border-2 border-[#953B0E]">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-[#953B0E] rounded-xl flex items-center justify-center">
                      <DollarSign className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-400">Monthly Commission</p>
                      <p className="text-xs text-gray-500">Platform earnings</p>
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-white mb-3">{formatCurrency(stats.monthlyRevenue)}</p>

                  {/* Comparisons */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-400">vs Last Month:</span>
                      <div className="flex items-center gap-1">
                        {calculateChange(stats.monthlyRevenue, stats.previousMonthRevenue) >= 0 ? (
                          <>
                            <TrendingUp className="w-4 h-4 text-green-500" />
                            <span className="text-green-500 font-medium">
                              +{calculateChange(stats.monthlyRevenue, stats.previousMonthRevenue).toFixed(1)}%
                            </span>
                          </>
                        ) : (
                          <>
                            <TrendingDown className="w-4 h-4 text-red-500" />
                            <span className="text-red-500 font-medium">
                              {calculateChange(stats.monthlyRevenue, stats.previousMonthRevenue).toFixed(1)}%
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-400">vs Last Year:</span>
                      <div className="flex items-center gap-1">
                        {calculateChange(stats.monthlyRevenue, stats.sameMonthLastYearRevenue) >= 0 ? (
                          <>
                            <TrendingUp className="w-4 h-4 text-green-500" />
                            <span className="text-green-500 font-medium">
                              +{calculateChange(stats.monthlyRevenue, stats.sameMonthLastYearRevenue).toFixed(1)}%
                            </span>
                          </>
                        ) : (
                          <>
                            <TrendingDown className="w-4 h-4 text-red-500" />
                            <span className="text-red-500 font-medium">
                              {calculateChange(stats.monthlyRevenue, stats.sameMonthLastYearRevenue).toFixed(1)}%
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                )}

                {/* Monthly Booking Revenue - admin sees this instead of commission */}
                {userRole !== 'superadmin' && (
                <div className="bg-[#1F2B1F] rounded-xl p-6 border-2 border-[#953B0E]">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-[#953B0E] rounded-xl flex items-center justify-center">
                      <DollarSign className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-400">Monthly Revenue</p>
                      <p className="text-xs text-gray-500">Total booking value</p>
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-white mb-3">{formatCurrency(stats.monthlyTotalBookingRevenue)}</p>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-400">vs Last Month:</span>
                      <div className="flex items-center gap-1">
                        {calculateChange(stats.monthlyTotalBookingRevenue, stats.previousMonthBookingRevenue) >= 0 ? (
                          <>
                            <TrendingUp className="w-4 h-4 text-green-500" />
                            <span className="text-green-500 font-medium">
                              +{calculateChange(stats.monthlyTotalBookingRevenue, stats.previousMonthBookingRevenue).toFixed(1)}%
                            </span>
                          </>
                        ) : (
                          <>
                            <TrendingDown className="w-4 h-4 text-red-500" />
                            <span className="text-red-500 font-medium">
                              {calculateChange(stats.monthlyTotalBookingRevenue, stats.previousMonthBookingRevenue).toFixed(1)}%
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-400">vs Last Year:</span>
                      <div className="flex items-center gap-1">
                        {calculateChange(stats.monthlyTotalBookingRevenue, stats.sameMonthLastYearBookingRevenue) >= 0 ? (
                          <>
                            <TrendingUp className="w-4 h-4 text-green-500" />
                            <span className="text-green-500 font-medium">
                              +{calculateChange(stats.monthlyTotalBookingRevenue, stats.sameMonthLastYearBookingRevenue).toFixed(1)}%
                            </span>
                          </>
                        ) : (
                          <>
                            <TrendingDown className="w-4 h-4 text-red-500" />
                            <span className="text-red-500 font-medium">
                              {calculateChange(stats.monthlyTotalBookingRevenue, stats.sameMonthLastYearBookingRevenue).toFixed(1)}%
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                )}

                {/* Monthly Bookings */}
                <div className="bg-[#1F2B1F] rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-[#2A382A] rounded-xl flex items-center justify-center">
                      <Users className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-400">Monthly Bookings</p>
                      <p className="text-xs text-gray-500">Total reservations</p>
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-white mb-3">{stats.monthlyBookings}</p>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-400">vs Last Month:</span>
                      <div className="flex items-center gap-1">
                        {calculateChange(stats.monthlyBookings, stats.previousMonthBookings) >= 0 ? (
                          <>
                            <TrendingUp className="w-4 h-4 text-green-500" />
                            <span className="text-green-500 font-medium">
                              +{calculateChange(stats.monthlyBookings, stats.previousMonthBookings).toFixed(1)}%
                            </span>
                          </>
                        ) : (
                          <>
                            <TrendingDown className="w-4 h-4 text-red-500" />
                            <span className="text-red-500 font-medium">
                              {calculateChange(stats.monthlyBookings, stats.previousMonthBookings).toFixed(1)}%
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-400">vs Last Year:</span>
                      <div className="flex items-center gap-1">
                        {calculateChange(stats.monthlyBookings, stats.sameMonthLastYearBookings) >= 0 ? (
                          <>
                            <TrendingUp className="w-4 h-4 text-green-500" />
                            <span className="text-green-500 font-medium">
                              +{calculateChange(stats.monthlyBookings, stats.sameMonthLastYearBookings).toFixed(1)}%
                            </span>
                          </>
                        ) : (
                          <>
                            <TrendingDown className="w-4 h-4 text-red-500" />
                            <span className="text-red-500 font-medium">
                              {calculateChange(stats.monthlyBookings, stats.sameMonthLastYearBookings).toFixed(1)}%
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Yearly Revenue - Only visible to superadmin */}
                {userRole === 'superadmin' && (
                <div className="bg-[#1F2B1F] rounded-xl p-6 border-2 border-green-600">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center">
                      <DollarSign className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-400">Yearly Revenue</p>
                      <p className="text-xs text-gray-500">{currentDate.getFullYear()}</p>
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-white">{formatCurrency(stats.yearlyRevenue)}</p>
                </div>
                )}

                {/* Average per Booking */}
                <div className="bg-[#1F2B1F] rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
                      <DollarSign className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-400">Avg per Booking</p>
                      <p className="text-xs text-gray-500">This month</p>
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-white">
                    {formatCurrency(stats.monthlyBookings > 0
                      ? (userRole === 'superadmin'
                        ? stats.monthlyRevenue / stats.monthlyBookings
                        : stats.monthlyTotalBookingRevenue / stats.monthlyBookings)
                      : 0)}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Per-tour commission breakdown + Export button - superadmin only */}
          {userRole === 'superadmin' && !loading && stats.tourBreakdown.length > 0 && (
            <div className="bg-[#1F2B1F] rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#953B0E] rounded-lg flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">Commission by Tour</h2>
                    <p className="text-xs text-gray-500">Breakdown for {monthName}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowReportModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-[#953B0E] text-white rounded-lg hover:bg-[#ff6a0d] transition-colors text-sm font-medium"
                >
                  <FileDown className="w-4 h-4" />
                  Export Report
                </button>
              </div>
              <div className="space-y-2">
                {stats.tourBreakdown.map((item) => (
                  <div key={item.tourId} className="flex items-center justify-between py-2 px-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-white truncate">{item.tourName}</p>
                      <p className="text-xs text-gray-500">{item.bookingCount} {item.bookingCount === 1 ? 'booking' : 'bookings'}</p>
                    </div>
                    <p className="text-sm font-semibold text-[#E85C0D] ml-4 shrink-0">{formatCurrency(item.commission)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Booking Calendar */}
          <div className="bg-[#1F2B1F] rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-white">Upcoming Bookings</h2>
              <Link
                to="/kh-admin/bookings"
                className="px-4 py-2 bg-[#953B0E] text-white rounded-lg hover:bg-[#ff6a0d] transition-colors text-sm font-medium"
              >
                Manage All Bookings
              </Link>
            </div>
            <BookingCalendar />
          </div>
        </div>
      </div>

      {showReportModal && (
        <CommissionReportModal onClose={() => setShowReportModal(false)} />
      )}
    </AdminLayout>
  );
};

export default Dashboard;